import {
  Box,
  Button,
  Chip,
  Paper,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Link } from "react-router-dom";

const titleHighlightSx = {
  position: "relative",
  display: "inline-block",
  "&::before": {
    content: '""',
    position: "absolute",
    left: "-0.12em",
    right: "-0.12em",
    bottom: "0.04em",
    height: "0.44em",
    bgcolor: "rgba(70, 168, 232, 0.22)",
    borderRadius: "0.45em 0.6em 0.4em 0.55em",
    transform: "rotate(-1deg)",
  },
};

function AdminFormFrame({
  title,
  eyebrow,
  description,
  icon,
  color = "#2196f3",
  bg = "#edf7ff",
  backHref = "/management",
  tips = [],
  children,
}) {
  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const Icon = icon;

  return (
    <Box
      sx={{
        width: "100%",
        mt: 4,
        mb: 6,
      }}
    >
      <Box component="header">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr auto",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: "16px",
                display: "grid",
                placeItems: "center",
                bgcolor: bg,
                color,
                flex: "0 0 auto",
              }}
            >
              {Icon && <Icon sx={{ fontSize: 36 }} />}
            </Box>
            <Box>
              <Chip
                size="small"
                label={eyebrow}
                sx={{ mb: 1.25, bgcolor: bg, color, fontWeight: 700 }}
              />
              <Typography
                component="h1"
                sx={{
                  color: "var(--toeic-ink-deep)",
                  fontSize: { xs: 34, md: 52 },
                  lineHeight: { xs: 1.05, md: 1 },
                  fontWeight: 800,
                  letterSpacing: 0,
                }}
              >
                <Box component="span" sx={titleHighlightSx}>
                  <Box component="span" sx={{ position: "relative" }}>
                    {title}
                  </Box>
                </Box>
              </Typography>
              <Typography sx={{ mt: 1.5, color: "var(--toeic-ink-soft)", maxWidth: 680, fontSize: 17, lineHeight: 1.65, fontWeight: 700 }}>
                {description}
              </Typography>
            </Box>
          </Box>
          <Button
            component={Link}
            to={backHref}
            startIcon={<ArrowBackIcon />}
            sx={{
              color: "#07111f",
              justifySelf: isMobile ? "start" : "end",
              borderRadius: "12px",
              "&:hover": { bgcolor: "#edf7ff", color: "#2196f3" },
            }}
          >
            Back
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 4, mb: 4, display: "grid", gap: 4 }}>
        <Box aria-hidden sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
        <Box
          className="toeic-reveal"
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: "18px",
            border: "1px solid rgba(185, 220, 255, 0.72)",
            bgcolor: "var(--toeic-paper-grid)",
            boxShadow: "var(--toeic-shadow-card)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ color: "var(--toeic-ink)", fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".12em" }}>
            Admin Form
          </Typography>
          <Typography sx={{ color, fontSize: 13, fontWeight: 700 }}>
            {tips.length} checklist items
          </Typography>
        </Box>
        <Box aria-hidden sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) 300px",
          gap: 3,
          alignItems: "start",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 2 : 3,
            borderRadius: "14px",
            border: 0,
            bgcolor: "var(--toeic-surface)",
            boxShadow: "var(--toeic-shadow-card)",
          }}
        >
          {children}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: "14px",
            border: "1.5px dotted var(--toeic-line)",
            bgcolor: "var(--toeic-paper-grid)",
            boxShadow: "var(--toeic-shadow-card)",
            position: isMobile ? "static" : "sticky",
            top: 92,
          }}
        >
          <Typography sx={{ color: "var(--toeic-ink-deep)", fontWeight: 700, mb: 1 }}>
            Checklist
          </Typography>
          <Box sx={{ display: "grid", gap: 1 }}>
            {tips.map((tip) => (
              <Box key={tip} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <CheckCircleIcon sx={{ color: "#35c759", fontSize: 20, mt: 0.1 }} />
                <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 14, fontWeight: 700 }}>{tip}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

export default AdminFormFrame;
